import {
  parseBoatNameTarget,
  type NeoLabelChange,
  type NeoLabelChangeRequest,
} from '@neomaris/protocol';

export type { NeoLabelChange, NeoLabelChangeRequest } from '@neomaris/protocol';
export const NEO_NAMES_UPDATED_EVENT = 'nm:boat-names-updated';

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function version(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function parseLabelChanges(value: unknown, boatId?: string): NeoLabelChange[] {
  const list = record(value) ? value.changes : value;
  if (!Array.isArray(list)) return [];
  return list.flatMap((raw) => {
    if (
      !record(raw) ||
      !text(raw.requestId) ||
      !text(raw.boatId) ||
      !text(raw.turnRef) ||
      !text(raw.beforeLabel) ||
      !text(raw.label) ||
      !version(raw.version)
    )
      return [];
    if (boatId !== undefined && raw.boatId !== boatId) return [];
    const target = parseBoatNameTarget(raw.target);
    if (
      target === null ||
      (raw.before !== null && typeof raw.before !== 'string') ||
      (raw.after !== null && typeof raw.after !== 'string')
    )
      return [];
    return [
      {
        requestId: raw.requestId,
        boatId: raw.boatId,
        target,
        before: raw.before as string | null,
        after: raw.after as string | null,
        beforeLabel: raw.beforeLabel,
        label: raw.label,
        version: raw.version,
        turnRef: raw.turnRef,
      },
    ];
  });
}

export function parseLabelRequests(value: unknown, boatId?: string): NeoLabelChangeRequest[] {
  const list = record(value) ? value.requests : value;
  if (!Array.isArray(list)) return [];
  return list.flatMap((raw) => {
    if (
      !record(raw) ||
      !text(raw.requestId) ||
      !text(raw.boatId) ||
      !text(raw.turnRef) ||
      !text(raw.beforeLabel) ||
      !text(raw.label) ||
      !version(raw.expectedVersion)
    )
      return [];
    if (boatId !== undefined && raw.boatId !== boatId) return [];
    const target = parseBoatNameTarget(raw.target);
    if (target === null || !['pending', 'applied', 'dismissed'].includes(String(raw.status)))
      return [];
    const change = parseLabelChanges(raw.change === undefined ? [] : [raw.change], raw.boatId)[0];
    if (raw.status === 'applied' && change === undefined) return [];
    return [
      {
        requestId: raw.requestId,
        boatId: raw.boatId,
        target,
        beforeLabel: raw.beforeLabel,
        label: raw.label,
        expectedVersion: raw.expectedVersion,
        turnRef: raw.turnRef,
        status: raw.status as NeoLabelChangeRequest['status'],
        ...(change === undefined ? {} : { change }),
      },
    ];
  });
}

export function labelConfirmErrorMessage(error: unknown): string {
  const status = record(error) ? error.status : undefined;
  if (status === 409) return 'This name changed after Neo made the card. Ask Neo again.';
  if (status === 404) return 'This name request is no longer available.';
  return 'Couldn’t change the name. Check the connection and try again.';
}
